import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import createSelectors from './createSelectors';

type localStoreBaseState = {
  cameraIP: string | undefined;
  teleophthResultsColsKeys: Record<string, boolean> | undefined;
  teleophthIntegratedResultsColsKeys: Record<string, boolean> | undefined;
  teleophthOrderResultsColsKeys: Record<string, boolean> | undefined;
  usersColsKeys: Record<string, boolean> | undefined;
  rememberUserEmail: string | undefined;
  isRememberEmail: boolean;
  hasExpandedSidebar: boolean;
  ssoState: string | undefined;
  selectedSiteId: string | undefined;
  gradingReceivedCount: number | undefined;
  gradingReceivedCountResetToken: number;
};

type localStoreBaseActions = {
  setCameraIP: (ip: string) => void;
  setTeleophthResultsColsKeys: (keys: Record<string, boolean>) => void;
  setTeleophthIntegratedResultsColsKeys: (
    keys: Record<string, boolean>,
  ) => void;
  setTeleophthOrderResultsColsKeys: (keys: Record<string, boolean>) => void;
  setUsersColsKeys: (keys: Record<string, boolean>) => void;
  setRememberUserEmail: (email: string | undefined) => void;
  setIsRememberEmail: (isRememberEmail: boolean) => void;
  setHasExpandedSidebar: (hasExpandedSidebar: boolean) => void;
  setSsoState: (ssoState: string) => void;
  setSelectedSiteId: (siteId: string | undefined) => void;
  setGradingReceivedCount: (count: number | undefined) => void;
  resetGradingReceivedCountPolling: () => void;
};

type localStoreBaseTypes = localStoreBaseState & localStoreBaseActions;

const localStoreBase = create<localStoreBaseTypes>()(
  immer(
    persist(
      (set) => ({
        cameraIP: undefined,
        teleophthResultsColsKeys: undefined,
        teleophthIntegratedResultsColsKeys: undefined,
        teleophthOrderResultsColsKeys: undefined,
        usersColsKeys: undefined,
        rememberUserEmail: undefined,
        isRememberEmail: false,
        hasExpandedSidebar: false,
        ssoState: undefined,
        selectedSiteId: undefined,
        gradingReceivedCount: undefined,
        gradingReceivedCountResetToken: 0,
        setCameraIP: (ip) => set({ cameraIP: ip }),
        setUsersColsKeys: (keys) => set({ usersColsKeys: keys }),
        setTeleophthResultsColsKeys: (keys) =>
          set({ teleophthResultsColsKeys: keys }),
        setTeleophthIntegratedResultsColsKeys: (keys) =>
          set({ teleophthIntegratedResultsColsKeys: keys }),
        setTeleophthOrderResultsColsKeys: (keys) =>
          set({ teleophthOrderResultsColsKeys: keys }),
        setRememberUserEmail: (email) => {
          set({
            rememberUserEmail: email,
            isRememberEmail: email !== undefined,
          });
        },
        setIsRememberEmail: (isRememberEmail) => set({ isRememberEmail }),
        setHasExpandedSidebar: (hasExpandedSidebar) =>
          set({ hasExpandedSidebar }),
        setSsoState(ssoState: string) {
          set({ ssoState });
        },
        setSelectedSiteId: (siteId) => set({ selectedSiteId: siteId }),
        setGradingReceivedCount: (count) =>
          set({ gradingReceivedCount: count }),
        resetGradingReceivedCountPolling: () => {
          set((state) => ({
            gradingReceivedCountResetToken:
              state.gradingReceivedCountResetToken + 1,
          }));
        },
      }),
      {
        name: 'optain-app-localStore',
        partialize: (state) => ({
          cameraIP: state.cameraIP,
          teleophthResultsColsKeys: state.teleophthResultsColsKeys,
          teleophthIntegratedResultsColsKeys:
            state.teleophthIntegratedResultsColsKeys,
          teleophthOrderResultsColsKeys: state.teleophthOrderResultsColsKeys,
          usersColsKeys: state.usersColsKeys,
          rememberUserEmail: state.rememberUserEmail,
          isRememberEmail: state.isRememberEmail,
          hasExpandedSidebar: state.hasExpandedSidebar,
          ssoState: state.ssoState,
          selectedSiteId: state.selectedSiteId,
          gradingReceivedCount: state.gradingReceivedCount,
          gradingReceivedCountResetToken: state.gradingReceivedCountResetToken,
        }),
      },
    ),
  ),
);

const useLocalStore = createSelectors(localStoreBase);

export default useLocalStore;
